import React, { useContext, useState } from 'react';
import { useIntl } from 'react-intl';

import { UseSubmitAction as useSubmitAction } from 'app/shared/utils/useSubmitAction';
import { City, EventCollection } from 'app/shared/typings';
import { AuthContext } from 'app/shared/context/Auth';
import { Box, Spinner, Typography } from 'app/shared/components/atoms';
import {
  Modal,
  ModalProps,
  TermsAndConditions,
} from 'app/shared/components/molecules';
import { CreateEventCollectionFollowing } from 'app/fan/graphql/eventCollectionFollowings/mutationHooks';

import { Form } from './Form';
import { NotifyMe } from './NotifyMe';
import { Success } from './Success';

interface Props {
  eventCollection: EventCollection;
  city: City;
}

export const FollowEventCollection: React.FC<
  React.PropsWithChildren<Props>
> = ({ eventCollection, city }) => {
  const { formatMessage } = useIntl();
  const { loggedIn } = useContext(AuthContext);
  const [isOpen, setIsOpen] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isSubmitting, setSubmitting] = useState(false);
  const [createEventCollectionFollowingAction] =
    CreateEventCollectionFollowing();

  const handleFollowEventCollection = useSubmitAction({
    submitAction: createEventCollectionFollowingAction,
    messageContext: 'alert',
    submitVariables: (values?: { email: string }) => ({
      email: values?.email,
      eventCollectionId: eventCollection.id,
      cityId: city.id,
      sourceUrl: window.location.href,
    }),
    failureMsg: formatMessage({ id: 'eventCollectionFollow.error' }),
    onSuccess: (response) => {
      if (response.data?.createEventCollectionFollowing?.success) {
        setIsSuccess(true);
      }
    },
  });

  if (loggedIn) {
    if (isSuccess) {
      return <Success />;
    }

    if (isSubmitting) {
      return <Spinner size="42px" invert />;
    }

    return (
      <NotifyMe
        onClick={() => {
          handleFollowEventCollection(undefined, {
            setSubmitting,
          });
        }}
      />
    );
  }

  const modalProps: Partial<ModalProps> = isSuccess
    ? {
        title: formatMessage({ id: 'shared.success' }),
        primaryCtaText: formatMessage({ id: 'shared.ok' }),
        onClickPrimaryCta: () => setIsOpen(false),
      }
    : {
        title: formatMessage(
          { id: 'eventCollectionFollow.formModalTitle' },
          { collection: eventCollection.title }
        ),
        detailText: <TermsAndConditions />,
      };
  const modalTextIntlId = isSuccess
    ? 'eventCollectionFollow.successModalText'
    : 'eventCollectionFollow.formModalText';

  return (
    <>
      {!isSuccess && <NotifyMe onClick={() => setIsOpen(true)} />}
      {isSuccess && <Success />}
      <Modal
        data-qaid="event-collection-follow-modal"
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        isPrimaryCtaFullWidth={false}
        maxWidth="400px"
        {...modalProps}
      >
        <Box flexDirection="column" gap={8}>
          <Typography>
            {formatMessage(
              { id: modalTextIntlId },
              { collection: eventCollection.title, city: city.title }
            )}
          </Typography>
          {!isSuccess && <Form onSubmit={handleFollowEventCollection} />}
        </Box>
      </Modal>
    </>
  );
};
